import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Download, X, Loader2, Printer } from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function PdfViewerModal({ url, title, onClose }) {
  const [pdfBlob, setPdfBlob] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageWidth, setPageWidth] = useState(700);

  const fetchPdf = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Erreur serveur (${res.status})`);
      const buf = await res.arrayBuffer();
      if (buf.byteLength < 50) throw new Error('PDF vide ou invalide');
      // Use Blob to prevent ArrayBuffer detachment issue with react-pdf worker
      const blob = new Blob([buf], { type: 'application/pdf' });
      setPdfBlob(blob);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [url]);
  
  // Memoize the file object to prevent re-renders causing detachment
  const pdfFile = useMemo(() => pdfBlob, [pdfBlob]);

  useEffect(() => { fetchPdf(); }, [fetchPdf]);

  useEffect(() => {
    const onResize = () => setPageWidth(Math.min(750, window.innerWidth - 64));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleDownload = () => {
    if (!pdfBlob) return;
    const blobUrl = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `${title || 'document'}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
  };

  const handlePrint = () => {
    if (!pdfBlob) return;
    const blobUrl = URL.createObjectURL(pdfBlob);
    const w = window.open(blobUrl);
    if (w) w.addEventListener('load', () => w.print());
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[60] flex flex-col" data-testid="pdf-viewer-modal">
      <div className="flex items-center justify-between bg-gray-900 text-white px-4 py-3 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-medium text-sm truncate">PDF — {title}</span>
          {numPages && <span className="text-xs text-gray-400 hidden sm:inline">({numPages} page{numPages > 1 ? 's' : ''})</span>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {pdfBlob && (
            <>
              <button onClick={handlePrint} className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-xs font-medium transition-colors" data-testid="pdf-print-btn">
                <Printer size={14} /> Imprimer
              </button>
              <button onClick={handleDownload} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded text-xs sm:text-sm font-medium transition-colors" data-testid="pdf-download-btn">
                <Download size={14} /> Télécharger
              </button>
            </>
          )}
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded transition-colors" data-testid="close-pdf-viewer">
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-gray-400 flex justify-center py-6 px-2">
        {loading && (
          <div className="flex flex-col items-center justify-center gap-3">
            <Loader2 className="animate-spin text-white" size={36} />
            <p className="text-sm font-medium text-white">Chargement du PDF...</p>
          </div>
        )}
        {error && !loading && (
          <div className="flex flex-col items-center justify-center gap-4 text-center px-4">
            <div className="bg-red-100 text-red-700 rounded-lg p-5 max-w-md">
              <p className="font-semibold mb-1">Impossible de charger le PDF</p>
              <p className="text-sm">{error}</p>
            </div>
            <button onClick={fetchPdf} className="px-5 py-2.5 bg-[#DC2626] text-white rounded-lg hover:bg-red-700 text-sm font-medium transition-colors">
              Réessayer
            </button>
          </div>
        )}
        {pdfFile && !error && (
          <Document
            file={pdfFile}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            onLoadError={(err) => setError(`Erreur de rendu: ${err.message}`)}
            loading={
              <div className="flex items-center gap-2 text-white">
                <Loader2 className="animate-spin" size={20} />
                <span className="text-sm">Rendu du document...</span>
              </div>
            }
          >
            {Array.from({ length: numPages || 0 }, (_, i) => (
              <div key={`page-${i}`} className="mb-4 shadow-xl">
                <Page
                  pageNumber={i + 1}
                  width={pageWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </div>
            ))}
          </Document>
        )}
      </div>
    </div>
  );
}
